import React from 'react';
import type { ConfiguredService } from '../../types/service.types';
import type { ConfiguredAddOn } from '../../types/addon.types';
import { useServicePlanSummary } from '../../hooks';
import { formatDateOnlyShort } from '@/app/utils/formatDate';
import { Badge, LoadingSpinner, QueryErrorAlert } from '@shared/components';
import type { BasketPricingState } from '@features/quote/hooks/useBasketPricing';
import { isUnrecoverablePricingError } from '@features/quote/utils/pricingErrors';

interface ServicePlanSummaryProps {
    configuredServices: ConfiguredService[];
    configuredAddOns: ConfiguredAddOn[];
    pricing: BasketPricingState;
    className?: string;
    // Quote-owned payment-schedule digest, rendered above the Service Schedule. The quote feature
    // owns its orchestration; this component only slots the resolved node in.
    paymentSummary?: React.ReactNode;
    // True while an upstream regime recompute (e.g. SMRiQ "Updating plan…") is in flight, before
    // the configured services even change — holds the cascade loaders through that window too.
    isServiceOrderUpdating?: boolean;
}

export const ServicePlanSummary: React.FC<ServicePlanSummaryProps> = ({
    configuredServices,
    configuredAddOns,
    pricing,
    className = 'sticky top-5',
    paymentSummary,
    isServiceOrderUpdating = false,
}) => {
    const { schedule } = useServicePlanSummary(configuredServices, configuredAddOns, pricing.data);

    const showCascadeLoader =
        pricing.error === null &&
        !pricing.notRequired &&
        (isServiceOrderUpdating || (schedule.length > 0 && (!pricing.data || pricing.isUpdating)));

    return (
        <div
            className={`rounded-lg border border-mp-border bg-mp-surface p-6 space-y-6 ${className}`}
        >
            <h2 className="text-xl font-bold text-mp-foreground mb-4 pb-2 border-b border-mp-border">
                Cost & Schedule Summary
            </h2>

            {/* Region stays mounted so it exists in the a11y tree before content
                appears; keep the wrapper unstyled so it self-collapses when empty. */}
            <div role="status">
                {pricing.isUpdating && (
                    <p
                        className="text-sm text-mp-muted"
                        data-testid="service-plan-summary-updating"
                    >
                        Updating price…
                    </p>
                )}
            </div>

            {pricing.error !== null && isUnrecoverablePricingError(pricing.error) && (
                <div data-testid="service-plan-summary-session-unavailable">
                    <QueryErrorAlert message="This service plan session is no longer available. Generate the plan again to price it." />
                </div>
            )}

            {pricing.error !== null && !isUnrecoverablePricingError(pricing.error) && (
                <div data-testid="service-plan-summary-error">
                    <QueryErrorAlert
                        message="Unable to price this basket."
                        onRetry={pricing.refetch}
                        isRetrying={pricing.isFetching}
                    />
                </div>
            )}

            {paymentSummary}

            <div>
                <h3
                    id="service-schedule-heading"
                    className="text-base font-semibold text-mp-foreground mb-3"
                >
                    Service Schedule
                </h3>
                {showCascadeLoader ? (
                    <LoadingSpinner message="Calculating service schedule..." className="py-6" />
                ) : schedule.length > 0 ? (
                    <ol aria-labelledby="service-schedule-heading" className="space-y-3">
                        {schedule.map((scheduleItem) => {
                            const isExtra = scheduleItem.type === 'Add-On';
                            return (
                                <li
                                    key={`${scheduleItem.type}-${scheduleItem.item.instanceId}`}
                                    className="grid grid-cols-[auto_minmax(0,1fr)] items-baseline gap-x-3 gap-y-1"
                                >
                                    <span className="font-bold text-mp-foreground">
                                        {formatDateOnlyShort(scheduleItem.startDate)}
                                    </span>
                                    <span
                                        className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 border-l-2 pl-3 ${
                                            isExtra ? 'border-mp-warning' : 'border-mp-info/20'
                                        }`}
                                    >
                                        <span className="font-semibold text-mp-foreground">
                                            {scheduleItem.item.name}
                                        </span>
                                        {isExtra && <Badge variant="warning">optional extra</Badge>}
                                    </span>
                                    {scheduleItem.linkedAddOns.length > 0 && (
                                        <ul className="col-start-2 space-y-1 border-l-2 border-transparent pl-3">
                                            {scheduleItem.linkedAddOns.map((addOn) => (
                                                <li
                                                    key={addOn.instanceId}
                                                    className="text-sm text-mp-foreground"
                                                >
                                                    + {addOn.name}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                ) : (
                    <p className="text-mp-muted text-sm">No items scheduled.</p>
                )}
            </div>
        </div>
    );
};
